import { Link } from 'react-router-dom';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { SERVICE_ORDER_STATUS } from '@etnrlz/shared';
import { useDashboard } from '../lib/queries';
import { Badge, Card, EmptyState, Loading, PageHeader, Stat } from '../components/ui';
import { Icon } from '../components/icons';
import { featureStatusMeta, osStatusMeta, preventiveStatusMeta } from '../components/status';
import { pluralDays } from '../lib/format';

export function DashboardPage() {
  const { data, isLoading } = useDashboard(30_000);
  if (isLoading || !data) return <Loading />;

  const chart = SERVICE_ORDER_STATUS.map((s) => ({
    status: osStatusMeta[s].label,
    color: osStatusMeta[s].color,
    n: data.osPorStatus[s] ?? 0,
  }));

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Visão geral da operação"
        actions={
          <Link to="/kiosk" className="text-sm text-muted hover:text-accent flex items-center gap-1.5">
            <Icon name="dashboard" className="h-4 w-4" /> Kiosk
          </Link>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="OS abertas" value={data.kpis.osAbertas} accent />
        <Stat label="Carros na garagem" value={data.kpis.carrosNaGaragem} />
        <Stat label="Peças abaixo do mín." value={data.kpis.pecasAbaixoMinimo} />
        <Stat label="Vencimentos" value={data.kpis.vencimentosProximos} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Carros parados */}
        <Card className="lg:col-span-2">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <Icon name="clock" className="h-4 w-4 text-accent" /> Carros parados
          </h2>
          {data.carrosParados.length === 0 ? (
            <EmptyState title="Nenhum carro parado" />
          ) : (
            <div className="space-y-2">
              {data.carrosParados.map((c) => (
                <Link
                  to={`/admin/vehicles/${c.vehicleId}`}
                  key={c.vehicleId}
                  className="flex items-center justify-between rounded-lg bg-surface-2 px-3 py-2.5 hover:bg-border/40 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.apelido}</div>
                    <div className="text-xs text-muted truncate">
                      {c.modelo} {c.osTitulo ? `· ${c.osTitulo}` : ''}
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <span className="num text-xl font-bold text-accent">{c.diasParado}</span>
                    <div className="text-[11px] text-muted">{pluralDays(c.diasParado)}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        {/* OS por status */}
        <Card>
          <h2 className="font-semibold mb-3">OS por status</h2>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chart} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <XAxis dataKey="status" tick={{ fontSize: 10, fill: '#8b97a7' }} interval={0} angle={-15} dy={8} height={40} />
              <Tooltip
                cursor={{ fill: '#ffffff08' }}
                contentStyle={{ background: '#161c27', border: '1px solid #232a36', borderRadius: 8, fontSize: 12 }}
              />
              <Bar dataKey="n" radius={[4, 4, 0, 0]}>
                {chart.map((d) => (
                  <Cell key={d.status} fill={d.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Alertas de vencimento */}
        <Card>
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <Icon name="alert" className="h-4 w-4 text-st-warn" /> Vencimentos
          </h2>
          {data.alertasVencimento.length === 0 ? (
            <EmptyState title="Tudo em dia" />
          ) : (
            <div className="space-y-2">
              {data.alertasVencimento.map((a, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="truncate">{a.apelido}</div>
                    <div className="text-xs text-muted truncate">{a.label}</div>
                  </div>
                  <Badge color={featureStatusMeta[a.status as 'VENCENDO']?.color}>
                    {a.diasRestantes < 0 ? `${Math.abs(a.diasRestantes)}d atrás` : `${a.diasRestantes}d`}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Preventivas */}
        <Card className="lg:col-span-2">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <Icon name="wrench" className="h-4 w-4 text-st-warn" /> Preventivas vencendo
          </h2>
          {data.preventivasVencendo.length === 0 ? (
            <EmptyState title="Nenhuma preventiva pendente" />
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {data.preventivasVencendo.map((p, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-surface-2 px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.titulo}</div>
                    <div className="text-xs text-muted truncate">{p.apelido}</div>
                  </div>
                  <Badge color={preventiveStatusMeta[p.status as 'VENCENDO']?.color}>
                    {preventiveStatusMeta[p.status as 'VENCENDO']?.label ?? p.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
